import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined'
import { useTranslation } from 'react-i18next'
import { projectService } from '@/services/projectService'
import type { ProjectParticipant } from '@/services/projectTypes'

type ExportScope = 'all' | 'completed'

type ExportStudyDialogProps = {
  open: boolean
  onClose: () => void
  projectId: number
  studyName: string
  participants: ProjectParticipant[]
  /** Metryczka answers per project participant id (from the study dashboard). */
  demographics: Record<number, Record<string, string>>
}

/** Turns the study name into a safe file-name prefix. */
function fileSlug(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return slug || 'study'
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

/** Quotes a CSV cell when it holds the delimiter, a quote or a line break. */
function csvCell(value: string): string {
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

/**
 * Semicolon-delimited CSV (matches the backend analysis export) with a BOM for
 * Excel. The metryczka questions become extra columns, in the order they
 * first appear across the respondents.
 */
function buildRespondentsCsv(
  participants: ProjectParticipant[],
  demographics: Record<number, Record<string, string>>,
): Blob {
  const metricColumns: string[] = []
  for (const p of participants) {
    for (const key of Object.keys(demographics[p.id] ?? {})) {
      if (!metricColumns.includes(key)) metricColumns.push(key)
    }
  }
  const header = [
    'respondent_id',
    'status',
    'assigned_at',
    'completed_at',
    'questionnaire_completed',
    ...metricColumns,
  ]
  const rows = participants.map((p) => [
    `R-#${p.id}`,
    p.status,
    p.assigned_at ?? '',
    p.completed_at ?? '',
    p.questionnaire_completed ? 'yes' : 'no',
    ...metricColumns.map((key) => demographics[p.id]?.[key] ?? ''),
  ])
  const csv = [header, ...rows].map((cols) => cols.map(csvCell).join(';')).join('\r\n')
  return new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
}

/**
 * Export modal for a single study: pick the data sets (per-frame emotion
 * analysis ZIP from the backend, respondents CSV built client-side) and the
 * respondent scope, then download.
 */
export default function ExportStudyDialog({
  open,
  onClose,
  projectId,
  studyName,
  participants,
  demographics,
}: ExportStudyDialogProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useTranslation('monitoring')

  const [withAnalysis, setWithAnalysis] = useState(true)
  const [withRespondents, setWithRespondents] = useState(true)
  const [scope, setScope] = useState<ExportScope>('all')
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const completedParticipants = useMemo(
    () => participants.filter((p) => p.status === 'completed'),
    [participants],
  )
  const scoped = scope === 'completed' ? completedParticipants : participants

  const nothingSelected = !withAnalysis && !withRespondents
  const emptyScope = scoped.length === 0

  const handleClose = () => {
    if (exporting) return
    setError(null)
    onClose()
  }

  const handleExport = async () => {
    setExporting(true)
    setError(null)
    try {
      if (withRespondents) {
        downloadBlob(buildRespondentsCsv(scoped, demographics), `${fileSlug(studyName)}_respondents.csv`)
      }
      if (withAnalysis) {
        const { blob, filename } = await projectService.exportProjectAnalysisZip(
          projectId,
          scoped.map((p) => p.id),
        )
        downloadBlob(blob, filename)
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('export.error'))
    } finally {
      setExporting(false)
    }
  }

  const optionSx = (selected: boolean, disabled = false) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.25,
    p: 1.5,
    borderRadius: '12px',
    border: '1px solid',
    borderColor: selected ? c.coral : theme.palette.divider,
    bgcolor: selected ? c.gray50 : 'transparent',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    transition: 'border-color 120ms, background-color 120ms',
    '&:hover': disabled ? {} : { borderColor: c.coral },
  })

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, pb: 0.5 }}>
        {t('export.title')}
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Typography sx={{ fontSize: 13.5, color: c.ink56, mb: 2 }}>
          {t('export.subtitle', { name: studyName })}
        </Typography>

        <Typography sx={{ fontSize: 12, fontWeight: 700, color: c.ink56, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
          {t('export.dataTitle')}
        </Typography>
        <Stack spacing={1} sx={{ mb: 2.5 }}>
          <Box sx={optionSx(withAnalysis)} onClick={() => setWithAnalysis((v) => !v)}>
            <Checkbox checked={withAnalysis} size="small" sx={{ p: 0, mt: '2px' }} />
            <InsightsOutlinedIcon sx={{ fontSize: 20, color: c.coral, mt: '2px' }} />
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: c.ink }}>
                {t('export.analysisLabel')}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>
                {t('export.analysisDesc')}
              </Typography>
            </Box>
          </Box>

          <Box sx={optionSx(withRespondents)} onClick={() => setWithRespondents((v) => !v)}>
            <Checkbox checked={withRespondents} size="small" sx={{ p: 0, mt: '2px' }} />
            <PeopleAltOutlinedIcon sx={{ fontSize: 20, color: c.coral, mt: '2px' }} />
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: c.ink }}>
                {t('export.respondentsLabel')}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>
                {t('export.respondentsDesc')}
              </Typography>
            </Box>
          </Box>

          <Box sx={optionSx(false, true)}>
            <Checkbox checked={false} disabled size="small" sx={{ p: 0, mt: '2px' }} />
            <SlideshowOutlinedIcon sx={{ fontSize: 20, color: c.ink32, mt: '2px' }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: c.ink }}>
                  {t('export.pptLabel')}
                </Typography>
                <Chip label={t('export.soon')} size="small" sx={{ height: 18, fontSize: 11, fontWeight: 600 }} />
              </Box>
              <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>
                {t('export.pptDesc')}
              </Typography>
            </Box>
          </Box>
        </Stack>

        <Typography sx={{ fontSize: 12, fontWeight: 700, color: c.ink56, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
          {t('export.scopeTitle')}
        </Typography>
        <RadioGroup row value={scope} onChange={(e) => setScope(e.target.value as ExportScope)} sx={{ flexWrap: 'wrap' }}>
          <FormControlLabel
            value="all"
            control={<Radio size="small" />}
            label={t('export.scopeAll', { count: participants.length })}
            slotProps={{ typography: { sx: { fontSize: 13.5 } } }}
          />
          <FormControlLabel
            value="completed"
            control={<Radio size="small" />}
            label={t('export.scopeCompleted', { count: completedParticipants.length })}
            slotProps={{ typography: { sx: { fontSize: 13.5 } } }}
          />
        </RadioGroup>

        {emptyScope && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {t('export.noRespondents')}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={exporting} color="inherit">
          {t('export.cancel')}
        </Button>
        <Button
          onClick={handleExport}
          disabled={exporting || nothingSelected || emptyScope}
          variant="contained"
          sx={{ borderRadius: 34, px: 2.5 }}
        >
          {exporting ? <CircularProgress size={18} color="inherit" /> : t('export.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
